/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api } from "../../../lib/auth";
import { toast } from "sonner";
import { UserCheck, Users, Plus, Loader2, Briefcase, X, TrendingUp, DollarSign } from "lucide-react";

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

export function TeamLeadsView() {
  const [items, setItems] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/t/re/team-leads").then((r) => setItems(r.data || []));
  useEffect(() => {
    load();
    api.get("/t/re/agencies").then((r) => setAgencies(r.data || []));
    api.get("/t/hrm/employees").then((r) => setEmployees((r.data || []).map((e) => ({ ...e, name: `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email })))).catch(() => {});
  }, []);

  return (
    <div data-testid="re-teamleads-view">
      <div className="flex items-center justify-end mb-4">
        <button onClick={() => setShow(true)} data-testid="re-teamlead-new-btn"
          className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2"><Plus size={14} /> New Team Lead</button>
      </div>
      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-black/8 p-12 text-center">
          <UserCheck size={32} className="mx-auto text-black/25 mb-3" />
          <div className="font-bold text-black/70">No team leads yet</div>
          <div className="text-xs text-black/45 mt-1">Every team lead must belong to an agency.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((tl) => (
            <div key={tl.id} className="bg-white rounded-2xl border border-black/8 p-5 hover:shadow-lg transition-shadow cursor-pointer" data-testid={`re-tl-card-${tl.id}`}
              onClick={() => setSelected(tl.id)}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-500 text-white flex items-center justify-center font-display font-extrabold text-lg">{(tl.name || "?")[0].toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-extrabold text-base text-black truncate">{tl.name}</div>
                  <div className="text-xs text-black/55 truncate flex items-center gap-1"><Briefcase size={11} /> {tl.agency_name}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${tl.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{tl.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                <Stat lbl="Marketers" v={tl.marketers_count || 0} />
                <Stat lbl="Sales" v={tl.sales_count || 0} />
                <Stat lbl="Revenue" v={ngn(tl.revenue)} small />
              </div>
            </div>
          ))}
        </div>
      )}
      {show && <TLEditor agencies={agencies} employees={employees} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {selected && <TLDetail tlid={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Stat({ lbl, v, small }) { return <div><div className="text-[10px] uppercase font-bold text-black/55">{lbl}</div><div className={`font-display font-extrabold text-black ${small ? "text-sm" : "text-lg"}`}>{v}</div></div>; }

function TLEditor({ agencies, employees, onClose, onSaved }) {
  const [f, setF] = useState({ employee_id: "", agency_id: agencies[0]?.id || "", override_commission_pct: 1.0 });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.agency_id) return toast.error("Agency is required");
    setSaving(true);
    try {
      await api.post("/t/re/team-leads", { ...f, override_commission_pct: parseFloat(f.override_commission_pct) || 0 });
      toast.success("Team Lead created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="re-tl-editor">
        <div className="flex items-center justify-between mb-4"><div className="font-display font-extrabold text-lg">New Team Lead</div><button onClick={onClose}><X size={16} /></button></div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase font-bold text-black/55">Promote from employee *</label>
            <select value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="re-tl-employee">
              <option value="">Select employee…</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.email}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase font-bold text-black/55">Assign to agency *</label>
            <select value={f.agency_id} onChange={(e) => setF({ ...f, agency_id: e.target.value })} className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="re-tl-agency">
              <option value="">Select agency…</option>
              {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase font-bold text-black/55">Override commission % (team lead bonus on team sales)</label>
            <input type="number" step="0.5" value={f.override_commission_pct} onChange={(e) => setF({ ...f, override_commission_pct: e.target.value })} className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="re-tl-override-pct" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-black/5 font-bold text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="re-tl-save"
            className="flex-1 py-2.5 rounded-lg bg-[#1FA868] text-white font-bold text-sm disabled:opacity-50">{saving ? "Saving…" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

function TLDetail({ tlid, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/t/re/team-leads/${tlid}`).then((r) => setData(r.data)).catch(() => {}); }, [tlid]);
  if (!data) return null;
  const { team_lead, marketers, sales, leads, team_stats } = data;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-violet-600 to-fuchsia-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-display font-extrabold text-2xl">{team_lead.name}</h2>
              <div className="text-sm opacity-85 mt-1">{team_lead.agency_name} · {team_lead.email}</div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-4 gap-3 mt-5">
            <KPI title="Marketers" value={team_stats.marketers} />
            <KPI title="Sales" value={team_stats.sales_count} />
            <KPI title="Revenue" value={ngn(team_stats.revenue)} />
            <KPI title="Leads" value={team_stats.leads_count} />
          </div>
        </div>
        <div className="p-6">
          <div className="font-display font-extrabold text-lg mb-3">Assigned marketers</div>
          {marketers.length === 0 ? (
            <div className="text-sm text-black/45 text-center py-6">No marketers under this team lead</div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {marketers.map((m) => (
                <div key={m.id} className="bg-[#FAFBFD] rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center font-bold">{(m.name || "?")[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{m.name}</div>
                    <div className="text-xs text-black/55">{m.sales_count} sales · {ngn(m.wallet_balance)} wallet</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="font-display font-extrabold text-lg mt-6 mb-3">Recent sales</div>
          <div className="space-y-1.5">
            {sales.slice(0, 8).map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm py-2 border-b border-black/5">
                <span>{s.property_title}</span>
                <span className="font-bold text-emerald-700">{ngn(s.sale_price)}</span>
              </div>
            ))}
            {sales.length === 0 && <div className="text-sm text-black/45 text-center py-4">No sales yet</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ title, value }) { return <div className="bg-white/15 backdrop-blur rounded-xl p-3"><div className="text-[10px] uppercase font-bold opacity-80">{title}</div><div className="font-display font-extrabold text-xl mt-1">{value}</div></div>; }
